import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Button } from '../../../../../components/ui/Button';
import { BulkOrderDialog } from './BulkOrderDialog';
import type { PreviewOrder } from './order-preview-fixtures';
import styles from './OrderPreviewDialog.module.css';

// Overlay visibility is local: opening a toolbar panel must not update the table.
export function OrderPreviewPopover({ kind, trigger, children }: {
  kind: 'filter' | 'fields';
  trigger: (open: boolean, toggle: () => void) => ReactNode;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const anchor = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    function outside(event: MouseEvent) {
      const target = event.target;
      // A clear button may unmount during its click handler. The original event
      // path still identifies it as an inside click after DOM containment changes.
      if (!(target instanceof Element) || anchor.current?.contains(target) || (anchor.current && event.composedPath().includes(anchor.current))) return;
      if (kind === 'filter' && target.closest('[role="option"], [role="listbox"]')) return;
      setOpen(false);
    }
    function escape(event: KeyboardEvent) {
      if (event.key !== 'Escape') return;
      if (kind === 'filter' && event.target instanceof Element && event.target.closest('[role="option"], [role="listbox"]')) return;
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
      anchor.current?.querySelector('button')?.focus();
    }
    document.addEventListener('click', outside);
    // Intercept before the outer drawer's document-level dismissal listener.
    window.addEventListener('keydown', escape, true);
    return () => {
      document.removeEventListener('click', outside);
      window.removeEventListener('keydown', escape, true);
    };
  }, [kind, open]);
  return <div className={kind === 'filter' ? styles.filterAnchor : styles.fieldsAnchor} ref={anchor}>
    {trigger(open, () => setOpen(current => !current))}
    {open ? <div aria-label={kind === 'filter' ? '订单筛选条件' : '显示字段选项'} className={kind === 'filter' ? styles.filterPanel : styles.fieldsPanel} id={kind === 'filter' ? 'preview-filter-panel' : 'preview-fields-panel'} role="dialog">{children}</div> : null}
  </div>;
}

export function BulkOrderAction({ orders, selected, onApply }: {
  orders: readonly PreviewOrder[];
  selected: readonly string[];
  onApply: (patch: Partial<PreviewOrder>) => void;
}) {
  const [open, setOpen] = useState(false);
  const anchor = useRef<HTMLSpanElement>(null);
  const selectedOrders = useMemo(() => {
    if (!open) return [];
    const keys = new Set(selected);
    return orders.filter(order => keys.has(order.id));
  }, [open, orders, selected]);
  function close() {
    setOpen(false);
    queueMicrotask(() => anchor.current?.querySelector('button')?.focus());
  }
  return <span ref={anchor}>
    <Button disabled={selected.length === 0} onClick={() => setOpen(true)} type="default">批量设置</Button>
    {open ? <BulkOrderDialog orders={selectedOrders} onClose={close} onApply={patch => { onApply(patch); close(); }} /> : null}
  </span>;
}
